import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

export interface ErrorReportContext {
  /** Product area, e.g. "payments" | "onboarding" | "chat" | "auth" | "firestore" | "render". */
  area: string;
  /** Page or component surface, e.g. "paywall_subscribe", "chat_thread". */
  surface?: string;
  /** Operation that failed, e.g. "start_checkout", "save_onboarding_answer". */
  op?: string;
  level?: "error" | "warning";
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  /** Optional logEvent name so the failure also lands in the analytics funnel. */
  analyticsEvent?: string;
}

/**
 * Report an error to Sentry and PostHog (and optionally the analytics
 * pipeline) with shared area/surface/op tagging. Reporting must never break
 * the app: every sink is wrapped, and both SDKs no-op safely before init.
 */
export function reportError(error: unknown, context: ErrorReportContext): void {
  const tags: Record<string, string> = {
    area: context.area,
    ...(context.surface ? { surface: context.surface } : {}),
    ...(context.op ? { op: context.op } : {}),
    ...context.tags,
  };

  try {
    Sentry.captureException(error, {
      level: context.level ?? "error",
      tags,
      extra: context.extra,
    });
  } catch {
    // Reporting must never break the app.
  }

  try {
    posthog.captureException(error, { ...tags, ...context.extra });
  } catch {
    // Reporting must never break the app.
  }

  if (context.analyticsEvent) {
    const analyticsEvent = context.analyticsEvent;
    const errorMessage = error instanceof Error ? error.message : String(error);
    // Lazy import: loggingService pulls in the Firebase SDK, which must not
    // become a static dependency of every reportError caller (it throws
    // without config, e.g. in unit tests).
    void import("../services/loggingService")
      .then(({ logEvent }) =>
        logEvent(analyticsEvent, {
          ...tags,
          errorMessage: errorMessage.slice(0, 300),
        })
      )
      .catch(() => undefined);
  }
}
